import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Input, Button, Badge, Spin, Modal, Form, message, Popconfirm } from 'antd';
import { 
  EnvironmentOutlined, 
  SearchOutlined, 
  PlusOutlined,
  CarOutlined,
  RocketOutlined,
  EyeOutlined,
  PercentageOutlined,
  MessageOutlined,
  MailOutlined,
  DollarOutlined,
  LineChartOutlined,
  ArrowLeftOutlined,
  TeamOutlined,
  SettingOutlined,
  ShopOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import axios from '../../../utils/axiosConfig';
import './Workspaces.css';

const Workspaces = () => {
  const { token } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [realWorkspaces, setRealWorkspaces] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [form] = Form.useForm();

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch real workspaces from API
  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/vehicle-tracking/workspaces', { headers: authHeader() });
      const workspaces = Array.isArray(data) ? data : (data.items || []);
      setRealWorkspaces(workspaces);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Fetch vehicle assignments to get vehicle counts per workspace
  const fetchAssignments = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/vehicle-tracking/assignments', { headers: authHeader() });
      const assignments = Array.isArray(data) ? data : (data.items || []);
      setVehicleAssignments(assignments);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchWorkspaces();
    fetchAssignments();
  }, [fetchWorkspaces, fetchAssignments]);

  // Create new workspace
  const handleCreateWorkspace = async (values) => {
    try {
      await axios.post('/api/vehicle-tracking/workspaces', values, { headers: authHeader() });
      message.success('Workspace created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchWorkspaces();
    } catch (err) {
      console.error('Error creating workspace:', err);
      message.error('Failed to create workspace');
    }
  };

  // Edit workspace
  const handleEditWorkspace = async (values) => {
    if (!editingWorkspace) return;
    try {
      await axios.put(`/api/vehicle-tracking/workspaces/${editingWorkspace.id}`, values, { headers: authHeader() });
      message.success('Workspace updated successfully');
      setEditModalVisible(false);
      setEditingWorkspace(null);
      form.resetFields();
      fetchWorkspaces();
    } catch (err) {
      console.error('Error updating workspace:', err);
      message.error('Failed to update workspace');
    }
  };

  // Delete workspace
  const handleDeleteWorkspace = async (workspaceId) => {
    try {
      await axios.delete(`/api/vehicle-tracking/workspaces/${workspaceId}`, { headers: authHeader() });
      message.success('Workspace deleted successfully');
      fetchWorkspaces();
    } catch (err) {
      console.error('Error deleting workspace:', err);
      message.error('Failed to delete workspace');
    }
  };

  // Open edit modal
  const openEditModal = (workspace) => {
    setEditingWorkspace(workspace);
    form.setFieldsValue({ name: workspace.name });
    setEditModalVisible(true);
  };

  // Get vehicle count for a workspace
  const getVehicleCount = (workspaceId) => {
    return vehicleAssignments.filter(a => a.workspace_id === workspaceId).length;
  };

  // Transform real workspaces to match the expected format
  const transformedRealWorkspaces = realWorkspaces.map(ws => ({
    id: `real-${ws.id}`,
    realId: ws.id,
    name: ws.name,
    address: 'Workspace Location',
    status: 'active',
    vehicles: getVehicleCount(ws.id),
    campaigns: 0,
    impressions: '0',
    impressionsPerMile: 0,
    combinedCTR: 0,
    sms: { rate: 0, color: '#8b5cf6' },
    email: { rate: 0, color: '#3b82f6' },
    meta: { rate: 0, color: '#ec4899' },
    google: { rate: 0, color: '#10b981' },
    isReal: true
  }));

  // Demo location data (shown after real workspaces)
  const demoLocations = [
    {
      id: 1,
      name: 'Atlanta Metro',
      address: '2500 Peachtree St NE, Atlanta, GA',
      status: 'active',
      vehicles: 10,
      campaigns: 8,
      impressions: '328.5K',
      impressionsPerMile: 90,
      combinedCTR: 4.1,
      sms: { rate: 4.1, color: '#8b5cf6' },
      email: { rate: 5.6, color: '#3b82f6' },
      meta: { rate: 2.5, color: '#ec4899' },
      google: { rate: 4.2, color: '#10b981' },
      analytics: {
        sms: [
          { label: 'Active Campaigns', value: 3, change: 12.5 },
          { label: 'Number Collected', value: '7.1K', change: 2.1 },
          { label: 'CTR', value: '4.2%', change: null },
          { label: 'Total Miles', value: '2.8K', change: null },
          { label: 'Impressions / Mile', value: '2.5', change: 8.2 }
        ],
        email: [
          { label: 'Active Campaigns', value: 2, change: 18.2 },
          { label: 'Emails Delivered', value: '13.3K', change: 3.2 },
          { label: 'Open Rate', value: '25.3%', change: 1.8 },
          { label: 'CTR', value: '5.8%', change: null },
          { label: 'Total Miles', value: '2.8K', change: 6.7 },
          { label: 'Impressions / Mile', value: '4.7', change: null }
        ],
        meta: [
          { label: 'Number of Campaigns', value: 2, change: 22.4 },
          { label: 'Total Spent', value: '$3,092', change: 6.4 },
          { label: 'CTR', value: '2.4%', change: null },
          { label: 'CPC', value: '$0.53', change: -8.2 },
          { label: 'Impressions', value: '243.0K', change: 15.3 },
          { label: 'Impressions / Mile', value: '86', change: 5.1 }
        ],
        google: [
          { label: 'Active Campaigns', value: 1, change: 18.2 },
          { label: 'Total Spent', value: '$1,456', change: 0.2 },
          { label: 'CTR', value: '3.9%', change: null },
          { label: 'CPC', value: '$0.89', change: -3.4 },
          { label: 'Impressions', value: '42.0K', change: 15.8 },
          { label: 'Impressions / Mile', value: '15', change: 7.2 }
        ]
      }
    },
    {
      id: 2,
      name: 'Marietta',
      address: '456 Cobb Parkway, Marietta, GA',
      status: 'active',
      vehicles: 4,
      campaigns: 4,
      impressions: '110.4K',
      impressionsPerMile: 82,
      combinedCTR: 3.12,
      sms: { rate: 3.5, color: '#8b5cf6' },
      email: { rate: 4.7, color: '#3b82f6' },
      meta: { rate: 1.8, color: '#ec4899' },
      google: { rate: 3.1, color: '#10b981' }
    },
    {
      id: 3,
      name: 'Gallatin',
      address: '200 W Main St, Gallatin, TN',
      status: 'inactive',
      vehicles: 2,
      campaigns: 0,
      impressions: '0',
      impressionsPerMile: 0,
      combinedCTR: 0,
      sms: { rate: 0, color: '#8b5cf6' },
      email: { rate: 0, color: '#3b82f6' },
      meta: { rate: 0, color: '#ec4899' },
      google: { rate: 0, color: '#10b981' }
    }
  ];

  // Combine real workspaces (on top) with demo locations
  const locations = [...transformedRealWorkspaces, ...demoLocations];

  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedLocation) {
    const location = locations.find(l => l.id === selectedLocation);
    return (
      <div className="location-detail-page">
        {/* Header */}
        <div className="location-detail-header">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => setSelectedLocation(null)}
            type="text"
            size="large"
          >
            Back to Locations
          </Button>
          <div className="last-updated">Last updated: 6:24:03 PM</div>
        </div>

        {/* Location Title */}
        <div className="location-title-section">
          <h1 className="location-title">{location.name}</h1>
          <p className="location-address">{location.address}</p>
        </div>

        {/* Location Overview Card */}
        <Card className="location-overview-card">
          <div className="location-overview-header">
            <div>
              <ShopOutlined style={{ fontSize: 24, marginRight: 12 }} />
              <span className="overview-title">Location Overview</span>
            </div>
            <span className="overview-subtitle">Performance metrics at a glance</span>
          </div>
          <Row gutter={24} className="location-overview-stats">
            <Col span={6}>
              <div className="location-stat-item">
                <CarOutlined className="stat-icon" />
                <div className="stat-value">{location.vehicles}</div>
                <div className="stat-label">Vehicles</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="location-stat-item">
                <RocketOutlined className="stat-icon" />
                <div className="stat-value">{location.campaigns}</div>
                <div className="stat-label">Active Campaigns</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="location-stat-item">
                <EyeOutlined className="stat-icon" />
                <div className="stat-value">{location.impressionsPerMile}</div>
                <div className="stat-label">Impressions / Mile</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="location-stat-item">
                <PercentageOutlined className="stat-icon" />
                <div className="stat-value">{location.combinedCTR}%</div>
                <div className="stat-label">Combined CTR</div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Analytics Overview */}
        <div className="location-analytics-section">
          <h2 className="section-title">Analytics Overview</h2>

          {location.analytics && (
            <>
              {/* SMS Analytics */}
              <div className="analytics-category">
                <h3 className="category-title">SMS ANALYTICS</h3>
                <Row gutter={16}>
                  {location.analytics.sms.map((item, idx) => (
                    <Col span={4} key={idx}>
                      <Card className="analytics-card">
                        <div className="analytics-icon"><MessageOutlined /></div>
                        <div className="analytics-value">{item.value}</div>
                        <div className="analytics-label">{item.label}</div>
                        {item.change && (
                          <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                            {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Email Analytics */}
              <div className="analytics-category">
                <h3 className="category-title">EMAIL ANALYTICS</h3>
                <Row gutter={16}>
                  {location.analytics.email.map((item, idx) => (
                    <Col span={4} key={idx}>
                      <Card className="analytics-card">
                        <div className="analytics-icon"><MailOutlined /></div>
                        <div className="analytics-value">{item.value}</div>
                        <div className="analytics-label">{item.label}</div>
                        {item.change && (
                          <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                            {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Meta Campaigns */}
              <div className="analytics-category">
                <h3 className="category-title">META CAMPAIGNS</h3>
                <Row gutter={16}>
                  {location.analytics.meta.map((item, idx) => (
                    <Col span={4} key={idx}>
                      <Card className="analytics-card">
                        <div className="analytics-icon"><DollarOutlined /></div>
                        <div className="analytics-value">{item.value}</div>
                        <div className="analytics-label">{item.label}</div>
                        {item.change && (
                          <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                            {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Google Analytics */}
              <div className="analytics-category">
                <h3 className="category-title">GOOGLE ANALYTICS</h3>
                <Row gutter={16}>
                  {location.analytics.google.map((item, idx) => (
                    <Col span={4} key={idx}>
                      <Card className="analytics-card">
                        <div className="analytics-icon"><LineChartOutlined /></div>
                        <div className="analytics-value">{item.value}</div>
                        <div className="analytics-label">{item.label}</div>
                        {item.change && (
                          <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                            {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}

          {/* Quick Actions */}
          <div className="location-quick-actions">
            <h3 className="section-title">Quick Actions</h3>
            <Row gutter={16}>
              <Col span={8}>
                <Card className="quick-action-card">
                  <div className="quick-action-icon">
                    <RocketOutlined style={{ color: '#3b82f6' }} />
                  </div>
                  <div className="quick-action-content">
                    <h4>Create Campaign</h4>
                    <p>Launch a new marketing campaign for this location</p>
                    <Button type="link">Create Campaign →</Button>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card className="quick-action-card">
                  <div className="quick-action-icon">
                    <TeamOutlined style={{ color: '#8b5cf6' }} />
                  </div>
                  <div className="quick-action-content">
                    <h4>Manage Team</h4>
                    <p>View and manage team members assigned to this location</p>
                    <Button type="link">View Team →</Button>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card className="quick-action-card">
                  <div className="quick-action-icon">
                    <SettingOutlined style={{ color: '#f59e0b' }} />
                  </div>
                  <div className="quick-action-content">
                    <h4>Location Settings</h4>
                    <p>Configure settings and preferences for this location</p>
                    <Button type="link">Settings →</Button>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="location-management-page">
      {/* Header */}
      <div className="location-header">
        <div>
          <h1 className="page-title">Location Management</h1>
          <p className="page-subtitle">Manage your business locations and their performance</p>
        </div>
        <div className="last-updated">Last updated: 6:24:03 PM</div>
      </div>

      {/* Title and Actions */}
      <div className="location-title-bar">
        <div>
          <h2 className="section-title">Location Management</h2>
          <p className="section-subtitle">
            {realWorkspaces.length > 0 
              ? `${realWorkspaces.length} location(s) from your account • Demo locations shown below`
              : 'Manage and monitor your business locations'
            }
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button 
            icon={<ReloadOutlined />} 
            size="large"
            onClick={() => { fetchWorkspaces(); fetchAssignments(); }}
            loading={loading}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setCreateModalVisible(true)}>
            Add Location
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="location-search">
        <Input
          placeholder="Search locations..."
          prefix={<SearchOutlined />}
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* Location Cards Grid */}
      {loading && <div style={{ textAlign: 'center', padding: 20 }}><Spin size="large" /></div>}
      <Row gutter={[16, 16]} className="location-cards-grid">
        {filteredLocations.map((location) => (
          <Col span={8} key={location.id}>
            <Card 
              className="location-card"
              onClick={() => setSelectedLocation(location.id)}
              hoverable
              style={location.isReal ? { borderTop: '3px solid #1890ff' } : {}}
            >
              {location.isReal && (
                <div style={{ position: 'absolute', top: 8, right: 8 }}>
                  <Badge count="Your Location" style={{ backgroundColor: '#1890ff' }} />
                </div>
              )}
              <div className="location-card-header">
                <div className="location-info">
                  <EnvironmentOutlined className="location-icon" style={location.isReal ? { color: '#1890ff' } : {}} />
                  <div>
                    <h3 className="location-name">{location.name}</h3>
                    <p className="location-address">{location.address}</p>
                  </div>
                </div>
                <Badge 
                  status={location.status === 'active' ? 'success' : 'default'} 
                  text={location.status === 'active' ? 'Active' : 'Inactive'}
                />
              </div>

              <div className="location-metrics">
                <div className="metric-item">
                  <CarOutlined />
                  <span className="metric-label">Vehicles</span>
                  <strong className="metric-value">{location.vehicles}</strong>
                </div>
                <div className="metric-item">
                  <RocketOutlined />
                  <span className="metric-label">Campaigns</span>
                  <strong className="metric-value">{location.campaigns}</strong>
                </div>
                <div className="metric-item">
                  <EyeOutlined />
                  <span className="metric-label">Impr</span>
                  <strong className="metric-value">{location.impressions}</strong>
                </div>
                <div className="metric-item">
                  <LineChartOutlined />
                  <span className="metric-label">Impr/Mi</span>
                  <strong className="metric-value">{location.impressionsPerMile}</strong>
                </div>
              </div>

              <div className="location-channels">
                <div className="channel-item">
                  <MessageOutlined style={{ color: location.sms.color }} />
                  <span className="channel-label">SMS</span>
                  <span className="channel-value">{location.sms.rate}%</span>
                </div>
                <div className="channel-item">
                  <MailOutlined style={{ color: location.email.color }} />
                  <span className="channel-label">Email</span>
                  <span className="channel-value">{location.email.rate}%</span>
                </div>
                <div className="channel-item">
                  <DollarOutlined style={{ color: location.meta.color }} />
                  <span className="channel-label">Meta</span>
                  <span className="channel-value">{location.meta.rate}%</span>
                </div>
                <div className="channel-item">
                  <LineChartOutlined style={{ color: location.google.color }} />
                  <span className="channel-label">Google</span>
                  <span className="channel-value">{location.google.rate}%</span>
                </div>
              </div>

              <div className="location-ctr">
                <span className="ctr-label">Combined CTR</span>
                <strong className="ctr-value">{location.combinedCTR}%</strong>
              </div>

              <div className="location-status-badge">
                <Badge 
                  status={location.status === 'active' ? 'success' : 'default'} 
                  text={location.status === 'active' ? 'Active' : 'Inactive'}
                />
              </div>

              {/* Edit/Delete buttons for real workspaces */}
              {location.isReal && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    icon={<EditOutlined />}
                    onClick={(e) => { e.stopPropagation(); openEditModal({ id: location.realId, name: location.name }); }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete Workspace"
                    description="Are you sure you want to delete this workspace?"
                    onConfirm={(e) => { e.stopPropagation(); handleDeleteWorkspace(location.realId); }}
                    onCancel={(e) => e.stopPropagation()}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Button 
                      size="small" 
                      danger 
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create Location Modal */}
      <Modal
        title="Create New Location"
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateWorkspace} layout="vertical">
          <Form.Item
            name="name"
            label="Location Name"
            rules={[{ required: true, message: 'Please enter location name' }]}
          >
            <Input placeholder="e.g. Miami Office, Denver Branch" />
          </Form.Item>
          <Form.Item name="city" label="City (Optional)">
            <Input placeholder="e.g. Miami" />
          </Form.Item>
          <Form.Item name="state" label="State (Optional)">
            <Input placeholder="e.g. FL" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Create Location
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Location Modal */}
      <Modal
        title="Edit Location"
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setEditingWorkspace(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} onFinish={handleEditWorkspace} layout="vertical">
          <Form.Item
            name="name"
            label="Location Name"
            rules={[{ required: true, message: 'Please enter location name' }]}
          >
            <Input placeholder="Location name" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Update Location
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Workspaces;
